'use client';

import { useAuth } from '@/context/auth';
import type { Buyer, Seller } from '@/graphql/graphql';
import { getBuyer } from '@/lib/api-buyers';
import { getSeller } from '@/lib/api-sellers';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ANONYMOUS_ID, type UserData, UserDataContext } from './user-data-context';

export function UserDataProvider({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();

    const [value, setValue] = useState<UserData | null>(null);

    useEffect(() => {
        const groups: string[] = (user?.profile['cognito:groups'] as string[]) ?? [];

        const isAdmin = groups.includes('admin') || groups.includes('admin_ro');
        const isBuyer = groups.includes('buyer');
        const isSeller = groups.includes('seller');
        const isAnonymous = !isAdmin && !isBuyer && !isSeller;
        const isReadOnly = groups.includes('admin_ro');

        const fetchData = async () => {
            if (isSeller) {
                const seller: Seller = await getSeller(user?.profile['custom:db_id'] as string);
                setValue({
                    name: seller?.contactName,
                    id: seller?.identifier.id,
                    user: seller,
                    isAdmin,
                    isBuyer,
                    isSeller,
                    isAnonymous,
                    isReadOnly,
                    loading: false,
                });
            } else if (isBuyer) {
                const buyer: Buyer = await getBuyer(user?.profile['custom:db_id'] as string);
                setValue({
                    name: buyer?.contactName,
                    id: buyer?.identifier.id,
                    user: buyer,
                    isAdmin,
                    isBuyer,
                    isSeller,
                    isAnonymous,
                    isReadOnly,
                    loading: false,
                });
            } else if (isAdmin) {
                setValue({
                    name: user?.profile.name as string,
                    id: user?.profile['cognito:username'] as string,
                    user: null,
                    isAdmin,
                    isBuyer,
                    isSeller,
                    isAnonymous,
                    isReadOnly,
                    loading: false,
                });
            } else {
                setValue({
                    name: 'anonymous',
                    id: ANONYMOUS_ID,
                    user: null,
                    isAdmin,
                    isBuyer,
                    isSeller,
                    isAnonymous,
                    isReadOnly,
                    loading: false,
                });
            }
        };

        if (!loading) {
            fetchData();
        }
    }, [user, loading]);

    useEffect(() => {
        if (!value || value.loading) return;

        if (value.isAnonymous) {
            datadogRum.clearUser();
        } else if (value.id) {
            const email =
                (value.user as Seller | Buyer | null)?.contactEmail ?? (user?.profile.email as string | undefined);

            datadogRum.setUser({
                id: value.id,
                name: value.name,
                email,
                role: value.isAdmin ? 'admin' : value.isSeller ? 'seller' : 'buyer',
            });
        }
    }, [value, user]);

    const memoizedValue = useMemo(() => value, [value]);

    if (!value) return children;

    return <UserDataContext.Provider value={memoizedValue as UserData}>{children}</UserDataContext.Provider>;
}
